import { Form, Link, useLocation } from "@remix-run/react";
import useScrollPosition from "~/hooks/useScrollPosition";
import { cn } from "~/lib/utils";
import { Dialog, DialogContent, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

type NavBarProps = {
  isTrialBannerOpen: boolean;
  isTrialing: boolean;
};

export default function NavBar({ isTrialBannerOpen, isTrialing }: NavBarProps) {
  const location = useLocation();
  const scrollPosition = useScrollPosition();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDialogOpen(false);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => setIsDialogOpen(false), [location]);

  if (location.pathname === "/billing/wait") return null;
  return (
    <>
      <nav
        className={cn(
          "w-full border-b border-transparent bg-transparent py-6 transition-colors",
          scrollPosition > 0 && "border-emerald-200 bg-emerald-50",
        )}
      >
        <div className="container flex w-full justify-between">
          <Link to="/">
            <img
              alt="knack sheet logo"
              src="/knack-sheet-logo.svg"
              className="hidden h-10 md:block"
            />
            <img
              alt="knack sheet logo"
              src="/logo-mark.svg"
              className="h-10 md:hidden"
            />
          </Link>
          <div className="hidden items-center gap-8 md:flex">
            <Link
              to="/dashboard"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Dashboard
            </Link>
            <Link
              to="/plugins"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Plugins
            </Link>
            <Link
              to="/documentation"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Documentation
            </Link>
            <Link
              to="/billing"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              {isTrialing ? "Pricing" : "Billing"}
            </Link>
            <Form method="post" action="/logout">
              <button type="submit">
                <p className="h-fit font-medium text-neutral-600 transition-colors hover:text-emerald-500">
                  Logout
                </p>
              </button>
            </Form>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsDialogOpen(true)}
          >
            <Menu />
          </Button>
        </div>
      </nav>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="left-auto right-4 top-4 w-72 translate-x-0 translate-y-0 rounded data-[state=closed]:slide-out-to-left-[0px] data-[state=closed]:slide-out-to-top-[0px] data-[state=open]:slide-in-from-left-[0px] data-[state=open]:slide-in-from-top-[0px] md:hidden">
          <button
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-500 dark:ring-offset-neutral-950 dark:focus:ring-neutral-300 dark:data-[state=open]:bg-neutral-800 dark:data-[state=open]:text-neutral-400"
            onClick={() => setIsDialogOpen(false)}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </button>
          <div className="flex flex-col items-start gap-8">
            <Link
              to="/dashboard"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Dashboard
            </Link>
            <Link
              to="/plugins"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Plugins
            </Link>
            <Link
              to="/documentation"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              Documentation
            </Link>
            <Link
              to="/billing"
              prefetch="intent"
              className="font-medium text-neutral-600 transition-colors hover:text-emerald-500"
            >
              {isTrialing ? "Pricing" : "Billing"}
            </Link>
            <Form method="post" action="/logout">
              <button type="submit">
                <p className="h-fit font-medium text-neutral-600 transition-colors hover:text-emerald-500">
                  Logout
                </p>
              </button>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
